function params(method, body, asFormData) {
  let header = {};
  if (body) {
    header = {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    };

    if (!asFormData) {
      header['Content-Type'] = 'application/json';
      body = JSON.stringify(body);
    }
    return { method: method, headers: header, body: body };
  } else {
    header = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    return { method: method, headers: header };
  }
}

// In-memory only (never persisted): lets a component that just resolved a
// version via activate() hand that result to the next render/route-effect so
// it doesn't have to re-resolve it. Re-resolving is not just redundant --
// for the major version specifically, the route has no `/v/:minor` segment,
// which is indistinguishable from "no version requested, restore whatever
// was last persisted" and can therefore land on the wrong version.
let lastActivated = null;

const versionService = {
  /**
   * The project's version list (major row + its duplicates) for the chip
   * dropdown, plus which real script id the caller is currently on.
   */
  getVersions: async function (rootScriptId) {
    const url = `${process.env.REACT_APP_API_URL}/scripts/versions/${rootScriptId}`;
    const response = await fetch(url, params('GET'));
    if (!response.ok) {
      throw new Error(`Failed to load versions: ${response.status}`);
    }
    return await response.json();
  },

  /**
   * Resolves + persists which version a user is on. Pass `minor` for a deep
   * link (/:rootScriptId/v/:minor); omit it to restore the user's last
   * active version (or the major version, if they have none saved yet).
   */
  activate: async function (rootScriptId, minor) {
    const url = `${process.env.REACT_APP_API_URL}/scripts/versions/${rootScriptId}/activate`;
    const response = await fetch(url, params('POST', { minor }));
    if (!response.ok) {
      const error = new Error(`Failed to activate version: ${response.status}`);
      error.status = response.status;
      throw error;
    }
    const data = await response.json();
    return data.version;
  },

  /**
   * Records that `rootScriptId` was just resolved to `version` so the very
   * next matching `consumeJustActivated` call (typically the route effect
   * that fires from the navigation right after this) can reuse it instead of
   * re-resolving from scratch. Consumed at most once.
   */
  rememberActivated: function (rootScriptId, version) {
    lastActivated = { rootScriptId: Number(rootScriptId), version };
  },

  /**
   * Returns and clears the version remembered via `rememberActivated` if it
   * matches `rootScriptId`, else null. One-shot by design -- a later
   * deep-link or page reload for the same project must always re-resolve
   * against the server, not silently reuse a stale in-memory value.
   */
  consumeJustActivated: function (rootScriptId) {
    if (lastActivated && lastActivated.rootScriptId === Number(rootScriptId)) {
      const version = lastActivated.version;
      lastActivated = null;
      return version;
    }
    return null;
  },

  /**
   * Duplicates whichever version the caller currently has active into a new
   * minor version and switches to it. Rejects with `{ code: 'PRO_REQUIRED' }`
   * on the response body if the user isn't on a Pro plan.
   */
  duplicate: async function (rootScriptId, label) {
    const url = `${process.env.REACT_APP_API_URL}/scripts/versions/${rootScriptId}/duplicate`;
    console.log('[versioning] versionService.duplicate: fetching', url);
    const response = await fetch(url, params('POST', { label }));
    console.log('[versioning] versionService.duplicate: got HTTP response', response.status);
    const data = await response.json().catch((err) => {
      console.error('[versioning] versionService.duplicate: response body was not JSON', err);
      return {};
    });
    if (!response.ok) {
      const error = new Error(data.message || `Failed to duplicate version: ${response.status}`);
      error.code = data.code;
      throw error;
    }
    return data.version;
  },
};

export default versionService;
